import * as THREE from "three";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import type { BaseState } from "./api.js";
import { cellToWorld } from "./grid.js";
import { RigidCrowd } from "./RigidCrowd.js";
import { t } from "../i18n/index.js";
import { toast } from "../ui/Toast.js";

const neighbours = (key: string): string[] => {
  const [x, y] = key.split(",").map(Number) as [number, number];
  return [`${x + 1},${y}`, `${x},${y + 1}`, `${x - 1},${y}`, `${x},${y - 1}`];
};

/** Pedestrians use opened paving cells and never cross buildings, planters or locked sectors. */
export function pedestrianCells(state: BaseState, gardens: ReadonlySet<string>): Set<string> {
  const cells = new Set<string>();
  for (const rect of [state.grid.core, ...state.zones.filter((z) => z.state === "open").map((z) => z.rect)]) {
    for (let y = rect.y; y < rect.y + rect.h; y++)
      for (let x = rect.x; x < rect.x + rect.w; x++) cells.add(`${x},${y}`);
  }
  for (const b of state.buildings)
    for (let y = b.y; y < b.y + b.h; y++) for (let x = b.x; x < b.x + b.w; x++) cells.delete(`${x},${y}`);
  for (const key of gardens) cells.delete(key);
  for (const d of state.loop?.decorations ?? []) for (let i = 0; i < (d.w ?? 1); i++) for (let j = 0; j < (d.h ?? 1); j++) cells.delete(`${d.x + i},${d.y + j}`);
  return cells;
}

export function visibleCitizenCount(population: number): number { return Math.min(18, 6 + Math.ceil(Math.max(0, population) / 10)); }

export function pedestrianPath(cells: ReadonlySet<string>, from: string, to: string): string[] {
  if (!cells.has(from) || !cells.has(to)) return [];
  const previous = new Map<string, string | null>([[from, null]]);
  const queue = [from];
  for (let i = 0; i < queue.length; i++) {
    const key = queue[i]!;
    if (key === to) {
      const path = [to];
      let cursor = previous.get(to);
      while (cursor) {
        path.unshift(cursor);
        cursor = previous.get(cursor);
      }
      return path;
    }
    for (const next of neighbours(key))
      if (cells.has(next) && !previous.has(next)) {
        previous.set(next, key);
        queue.push(next);
      }
  }
  return [];
}

interface Citizen {
  outer: THREE.Group;
  model: ModelInstance;
  cell: string;
  path: string[];
  wait: number;
  sequence: number;
  walking: boolean;
}

/** Small maintenance crews give the settlement human scale. Routes adapt whenever its layout changes. */
export class Citizens {
  readonly group = new THREE.Group();
  private actors: Citizen[] = [];
  private cells = new Set<string>();
  private keys: string[] = [];
  private size = 24;
  private signature = "";
  private disposed = false;
  private crowd: RigidCrowd | null = null;
  private reduced = false;
  private visibleCount = 6;

  constructor() {
    void loadModel("prop.prop_survivor")
      .then((template) => {
        if (this.disposed) return;
        for (let i = 0; i < 18; i++) {
          const outer = new THREE.Group();
          const model = instantiate(template);
          model.object.scale.setScalar(0.68 + (i % 3) * 0.04);
          outer.add(model.object);
          this.actors.push({
            outer,
            model,
            cell: "",
            path: [],
            wait: i * 0.5,
            sequence: i * 31,
            walking: false,
          });
          model.play("idle");
        }
        this.crowd = new RigidCrowd(this.actors.map((actor) => actor.outer));
        this.group.add(this.crowd.group);
        this.placeActors();
      })
      .catch((error: unknown) => {
        if (this.disposed) return;
        console.error("Could not load colony residents", error);
        toast.error(t("base.residentsUnavailable"));
      });
  }

  update(state: BaseState, gardens: ReadonlySet<string>): void {
    const cells = pedestrianCells(state, gardens);
    this.visibleCount = visibleCitizenCount(state.loop?.morale.population ?? 0);
    const signature = `${this.visibleCount}|${[...cells].join(";")}`;
    if (signature === this.signature) return;
    this.signature = signature;
    this.cells = cells;
    this.keys = [...cells];
    this.size = state.grid.size;
    this.placeActors();
  }

  private point(key: string): THREE.Vector3 {
    const [x, y] = key.split(",").map(Number) as [number, number];
    const { wx, wz } = cellToWorld(x, y, 1, 1, this.size);
    return new THREE.Vector3(wx, 0.04, wz);
  }

  private placeActors(): void {
    this.actors.forEach((actor, i) => {
      actor.outer.visible = this.keys.length > 0 && i < this.visibleCount;
      if (!this.keys.length) return;
      actor.cell = this.cells.has(actor.cell) ? actor.cell : this.keys[(i * 17 + 7) % this.keys.length]!;
      actor.outer.position.copy(this.point(actor.cell));
      actor.path = [];
      actor.wait = 0.5 + i * 0.2;
      actor.walking = false;
      actor.model.play("idle");
    });
    this.crowd?.sync();
  }

  tick(dt: number, reducedMotion: boolean): void {
    if (!this.group.visible || !this.keys.length) return;
    if (reducedMotion !== this.reduced) {
      this.reduced = reducedMotion;
      if (reducedMotion)
        for (const actor of this.actors) {
          actor.model.mixer.stopAllAction();
          actor.model.play("idle", { fade: 0 });
          actor.model.mixer.update(0);
          actor.walking = false;
        }
    }
    for (const actor of this.actors) {
      if (reducedMotion || !actor.outer.visible) continue;
      actor.model.mixer.update(dt);
      if (actor.wait > 0) {
        actor.wait -= dt;
        continue;
      }
      if (!actor.path.length) {
        for (let tries = 0; tries < 12; tries++) {
          actor.sequence += 37;
          const target = this.keys[actor.sequence % this.keys.length]!;
          const path = pedestrianPath(this.cells, actor.cell, target);
          if (path.length > 3) {
            actor.path = path.slice(1);
            break;
          }
        }
        if (!actor.path.length) {
          actor.wait = 3;
          continue;
        }
      }
      if (!actor.walking) {
        actor.model.play("walk");
        actor.walking = true;
      }
      const target = this.point(actor.path[0]!);
      const direction = target.clone().sub(actor.outer.position);
      const distance = direction.length();
      actor.outer.rotation.y = Math.atan2(direction.x, direction.z);
      if (distance <= dt * 0.5) {
        actor.outer.position.copy(target);
        actor.cell = actor.path.shift()!;
        if (!actor.path.length) {
          actor.wait = 2 + (actor.sequence % 4);
          actor.walking = false;
          actor.model.play("idle");
        }
      } else actor.outer.position.addScaledVector(direction, (dt * 0.5) / distance);
    }
    this.crowd?.sync();
  }

  dispose(): void {
    this.disposed = true;
    for (const actor of this.actors) actor.model.mixer.stopAllAction();
    this.crowd?.dispose();
    this.crowd = null;
    this.actors = [];
    this.group.clear();
  }
}
